import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

interface Colors {
  background: string;
  surface: string;
  surfaceSecondary: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  codeBackground: string;
}

export interface DSColors {
  primary: string;
  primaryStrong: string;
  secondary: string;
  accent: string;
  success: string;
  successStrong: string;
  warning: string;
  warningStrong: string;
  error: string;
  pulseRed: string;
  vitalWhite: string;
  asphaltBlack: string;
  treadGray: string;
  signalBlue: string;
}

type ThemeContextType = {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  theme: 'dark' | 'light';
  colors: Colors;
  dsColors: DSColors;
};

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize from localStorage if available, otherwise default to dark mode
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedMode = localStorage.getItem('rp-darkMode');
    return savedMode !== null ? JSON.parse(savedMode) : true;
  });

  // Define theme colors
  const theme = isDarkMode ? 'dark' : 'light';
  
  // RidePulse brand colors - memoized to prevent re-renders
  const dsColors = useMemo(() => ({
    primary: '#E63946', // rp-pulse-red
    primaryStrong: '#D12A36', // primary darkened only where white text sits on it (4.17:1 -> 5.13:1)
    secondary: '#0077FF', // rp-signal-blue  
    accent: '#E63946', // rp-pulse-red
    success: '#10b981',
    successStrong: '#0d9668', // success darkened for icon/small-element use (>=3:1 on light & dark surfaces)
    warning: '#f59e0b',
    warningStrong: '#d97706', // warning darkened for icon/small-element use (>=3:1 on light & dark surfaces)
    error: '#ef4444',
    pulseRed: '#E63946',
    vitalWhite: '#F7F9FA',
    asphaltBlack: '#121212',
    treadGray: '#6E6E73',
    signalBlue: '#0077FF'
  }), []);

  // Theme-specific colors
  const colors: Colors = useMemo(() => isDarkMode 
    ? {
        background: '#0a0a0a',
        surface: '#1a1a1a',
        surfaceSecondary: '#141414',
        border: 'rgba(255,255,255,0.08)',
        textPrimary: '#ffffff',
        textSecondary: '#cccccc',
        textTertiary: '#999999',
        codeBackground: '#2d2d2d'
      }
    : {
        background: '#f8f9fa',
        surface: '#ffffff',
        surfaceSecondary: '#f1f4f8',
        border: 'rgba(0,0,0,0.1)',
        textPrimary: '#111827',
        textSecondary: '#4b5563',
        textTertiary: '#6b7280',
        codeBackground: '#e5e7eb'
      }, [isDarkMode]);

  // Update localStorage and CSS variables when isDarkMode changes
  useEffect(() => {
    localStorage.setItem('rp-darkMode', JSON.stringify(isDarkMode));
    
    // Update CSS custom properties
    const root = document.documentElement;
    root.style.setProperty('--rp-bg', colors.background);
    root.style.setProperty('--rp-surface', colors.surface);
    root.style.setProperty('--rp-surface-secondary', colors.surfaceSecondary);
    root.style.setProperty('--rp-text', colors.textPrimary);
    root.style.setProperty('--rp-text-secondary', colors.textSecondary);
    root.style.setProperty('--rp-text-tertiary', colors.textTertiary);
    root.style.setProperty('--rp-primary', dsColors.primary);
    root.style.setProperty('--rp-secondary', dsColors.secondary);
    root.style.setProperty('--rp-accent', dsColors.accent);
    root.style.setProperty('--rp-border', colors.border);
    root.style.setProperty('--rp-border-focus', isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)');
    
    // Fixed text colors for dark backgrounds (always light regardless of theme)
    root.style.setProperty('--rp-text-on-dark', '#ffffff');
    root.style.setProperty('--rp-text-secondary-on-dark', '#cccccc');
    root.style.setProperty('--rp-text-tertiary-on-dark', '#999999');
    
    // Glass navigation tokens
    root.style.setProperty('--rp-glass-bg', isDarkMode ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)');
    root.style.setProperty('--rp-glass-border', isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)');
    root.style.setProperty('--rp-glass-blur', '20px');
    
    // Apply appropriate class to html element
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode, colors, dsColors]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, theme, colors, dsColors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
